/**
 * 退職時スキャン `/dlp/scans`。退職イベントで自動に、または「スキャンを依頼」で手動に作られる。
 *
 * 一覧テンプレート(`ui/`): PageHeader + FilterBar + DataTable + 右パネル(`?panel=<id>`)。
 * 状態はビュー(chips)、端末・従業員・契機は条件、文字検索はサーバーに無いので取得した先頭 200 件を
 * 手元で絞る(hr の申請一覧と同じ流儀。`PartialResultNote` で切れていることを出す)。
 */
import { Alert, Button, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { DlpScan } from "../../lib/api";
import {
  DataTable,
  FilterBar,
  PARTIAL_CLEAR_LABEL,
  PageHeader,
  PartialResultNote,
  PopClose,
  TimeAgo,
  partialResultText,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type ConditionDef,
  type DataColumn,
} from "../../ui";
import { deviceCell, employeeCell, scanStatusTag, triggerTag } from "./bits";
import { SCAN_STATUSES, SCAN_TRIGGERS, triggerLabel } from "./constants";
import { errorText, useDlpOverview, useDlpPerms, useDlpScans } from "./hooks";
import { ScanPanel } from "./ScanPanel";
import { ScanRequestModal } from "./ScanRequestModal";
import { DeviceSelect, EmployeeSelect } from "./selects";

const LOCAL_LIMIT = 200;

/** 検索語(空白区切り AND)が、行の文字列のどこかに含まれるか。 */
function matches(q: string, ...fields: (string | number | null | undefined)[]): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hay = fields.map((f) => String(f ?? "").toLowerCase()).join(" ");
  return terms.every((t) => hay.includes(t));
}

export function ScansPage() {
  const { canOperate } = useDlpPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const status = sp.get("status") ?? "";
  const deviceId = sp.get("device_id") ?? "";
  const employeeId = sp.get("employee_id") ?? "";
  const trigger = sp.get("trigger") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [requesting, setRequesting] = useState(false);

  const local = Boolean(q);
  const filters: Record<string, string> = {
    ...(status ? { status } : {}),
    ...(deviceId ? { device_id: deviceId } : {}),
    ...(employeeId ? { employee_id: employeeId } : {}),
    ...(trigger ? { trigger } : {}),
  };
  const query = useDlpScans(
    local
      ? { ...filters, limit: String(LOCAL_LIMIT), offset: "0" }
      : { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
  );
  // ビューの件数は概要 API から(件数の口を別に足さない)。cancelled は概要に無いので出さない
  const overview = useDlpOverview();
  const counts: Record<string, number | undefined> = overview.data
    ? {
        all: Object.values(overview.data.scans).reduce((a, b) => a + b, 0),
        ...overview.data.scans,
      }
    : {};
  const filterKey = `${status}|${deviceId}|${employeeId}|${trigger}|${q}`;
  useEffect(() => setPage(1), [filterKey]);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!local) return items;
    return items.filter((r) =>
      matches(q, r.id, r.device?.hostname, r.device?.serial, r.employee?.name, r.employee?.emp_code, r.requested_by, r.note),
    );
  }, [query.data, local, q]);
  const total = local ? rows.length : (query.data?.total ?? 0);
  const fetched = query.data?.items.length ?? 0;
  const partial = local && (query.data?.total ?? 0) > LOCAL_LIMIT;

  const columns: DataColumn<DlpScan>[] = [
    { key: "id", title: "#", ellipsis: false, dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    { key: "device", title: "端末", ellipsis: false, width: 190, render: (_: unknown, r) => deviceCell(r.device) },
    { key: "employee", title: "従業員", ellipsis: false, width: 190, render: (_: unknown, r) => employeeCell(r.employee) },
    { key: "status", title: "状態", ellipsis: false, width: 110, render: (_: unknown, r) => scanStatusTag(r) },
    { key: "findings", title: "検出", ellipsis: false, dataIndex: "findings_count", width: 90, render: (v: number) => <span className="ui-num">{v}</span> },
    { key: "trigger", title: "契機", ellipsis: false, width: 110, render: (_: unknown, r) => triggerTag(r.trigger) },
    {
      key: "requested",
      title: "依頼",
      ellipsis: false,
      width: 180,
      render: (_: unknown, r) => (
        <div>
          <div>{r.requested_by || (r.trigger === "offboard" ? "退職イベント" : "")}</div>
          <div className="ui-cell-sub">
            <TimeAgo iso={r.created_at} />
          </div>
        </div>
      ),
    },
    { key: "finished", title: "完了", ellipsis: false, width: 160, render: (_: unknown, r) => (r.finished_at ? <TimeAgo iso={r.finished_at} /> : "") },
  ];
  // パネル開時は識別に要る列だけに絞る
  const visibleColumns = panelId
    ? columns.filter((c) => ["id", "device", "status", "findings"].includes(String(c.key)))
    : columns;

  // 条件チップの表示文字は取得した行から拾う(URL には id しか無い)
  const deviceLabel = deviceId ? (rows.find((r) => String(r.device_id) === deviceId)?.device?.hostname ?? `#${deviceId}`) : "";
  const employeeLabel = employeeId
    ? (rows.find((r) => String(r.employee_id) === employeeId)?.employee?.name ?? `#${employeeId}`)
    : "";
  const conditions: ConditionDef[] = [
    {
      key: "device_id",
      label: "端末",
      value: deviceLabel,
      onClear: () => patch({ device_id: null }, true),
      control: (close) => (
        <>
          <DeviceSelect
            value={deviceId ? Number(deviceId) : null}
            onChange={(id) => {
              patch({ device_id: id ? String(id) : null }, true);
              if (id) close();
            }}
            autoFocus
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "employee_id",
      label: "従業員",
      value: employeeLabel,
      onClear: () => patch({ employee_id: null }, true),
      control: (close) => (
        <>
          <EmployeeSelect
            value={employeeId ? Number(employeeId) : null}
            onChange={(id) => {
              patch({ employee_id: id ? String(id) : null }, true);
              if (id) close();
            }}
            autoFocus
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "trigger",
      label: "契機",
      value: trigger ? triggerLabel(trigger) : "",
      onClear: () => patch({ trigger: null }, true),
      control: (close) => (
        <>
          <Select
            autoFocus
            style={{ minWidth: 160 }}
            value={trigger || undefined}
            placeholder="契機"
            options={SCAN_TRIGGERS}
            onChange={(v) => {
              patch({ trigger: v ?? null }, true);
              close();
            }}
          />
          <PopClose onClick={close} />
        </>
      ),
    },
  ];
  const filtered = Boolean(q) || Boolean(deviceId) || Boolean(employeeId) || Boolean(trigger);
  const clearAll = () => {
    setQInput("");
    patch({ q: null, device_id: null, employee_id: null, trigger: null }, true);
  };

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="退職時スキャン"
          description="退職の申請が完了した従業員の端末で、社外秘などのキーワードを含むファイルを探す。結果は次の poll でエージェントに渡り、report で戻る。PDF は読まない(v0)。"
          primary={
            canOperate ? (
              <Button type="primary" onClick={() => setRequesting(true)}>
                スキャンを依頼
              </Button>
            ) : undefined
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "ホスト名 / 製造番号 / 従業員 / 依頼者" }}
          views={{
            items: [
              { key: "all", label: "すべて", count: counts.all },
              ...SCAN_STATUSES.map((s) => ({
                key: s.value,
                label: s.label,
                count: counts[s.value],
                tone: s.value === "done" && (counts.done ?? 0) > 0 ? ("warning" as const) : undefined,
              })),
            ],
            active: status || "all",
            onChange: (key) => patch({ status: key === "all" ? null : key }, true),
          }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="スキャンを取得できなかった" description={errorText(query.error)} />
        )}
        {partial && <PartialResultNote limit={LOCAL_LIMIT} onClear={clearAll} />}
        <DataTable<DlpScan>
          columns={visibleColumns}
          scrollX={visibleColumns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          countLabel={partial ? `${fetched} 件中 ${rows.length} 件` : undefined}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          rowTone={(row) => (row.status === "error" ? "danger" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: `${filtered || status ? "条件に合うスキャンはない" : "スキャンはまだない。退職の申請が完了すると自動で作られる"}${
              partial ? `(${partialResultText(LOCAL_LIMIT)})` : ""
            }`,
            action: partial ? (
              <Button onClick={clearAll}>{PARTIAL_CLEAR_LABEL}</Button>
            ) : filtered ? (
              <Button onClick={clearAll}>条件を外す</Button>
            ) : canOperate ? (
              <Button type="primary" onClick={() => setRequesting(true)}>
                スキャンを依頼
              </Button>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <ScanPanel id={Number(panelId)} onClose={() => setPanelId(null)} />}
      <ScanRequestModal
        open={requesting}
        onClose={() => setRequesting(false)}
        onCreated={(scan) => {
          setRequesting(false);
          setPanelId(String(scan.id));
        }}
      />
    </div>
  );
}
